import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Gestione categorie unificata per Food & Beverage e Attrezzature & Packaging.
 * - Elenco categorie con modifica inline e cancellazione (solo se vuota)
 * - Aggiunta nuova categoria
 *
 * Props:
 *   open / onOpenChange: controllo dialog
 *   isFood: true = categorie-ingredienti, false = materiali/categorie
 *   categorie: array categorie già caricate
 *   localeId: stringa
 *   onRefresh: callback dopo ogni modifica
 *   prodotti: array prodotti (per controllo "categoria vuota")
 */
const GestioneCategorie = ({ open, onOpenChange, isFood, categorie, localeId, onRefresh, prodotti = [] }) => {
  const { token } = useAuth();

  // Nuova categoria
  const [nuovoNome, setNuovoNome] = useState("");
  const [nuovaIcona, setNuovaIcona] = useState(isFood ? "🥗" : "📦");
  const [addingNew, setAddingNew] = useState(false);

  // Modifica inline
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [editIcona, setEditIcona] = useState("");

  const [saving, setSaving] = useState(false);

  const baseUrl = isFood ? `${API}/api/categorie-ingredienti` : `${API}/api/materiali/categorie`;
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const prodottiPerCategoria = (catId) =>
    prodotti.filter(p => p.categoria_id === catId).length;

  // ── Aggiungi ──
  const handleAdd = async () => {
    if (!nuovoNome.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      const body = isFood
        ? { nome: nuovoNome.trim(), locale_id: localeId }
        : { nome: nuovoNome.trim(), icona: nuovaIcona, locale_id: localeId };
      const res = await fetch(baseUrl, { method: "POST", headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success("Categoria aggiunta");
      setNuovoNome("");
      setNuovaIcona(isFood ? "🥗" : "📦");
      setAddingNew(false);
      onRefresh();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  // ── Modifica ──
  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditNome(cat.nome);
    setEditIcona(cat.icona || (isFood ? "🥗" : "📦"));
  };

  const handleSaveEdit = async (cat) => {
    if (!editNome.trim()) { toast.error("Il nome non può essere vuoto"); return; }
    setSaving(true);
    try {
      let res;
      if (isFood) {
        // PUT usa query param per categorie-ingredienti
        res = await fetch(`${baseUrl}/${cat.id}?nome=${encodeURIComponent(editNome.trim())}`, {
          method: "PUT", headers,
        });
      } else {
        res = await fetch(`${baseUrl}/${cat.id}`, {
          method: "PUT", headers,
          body: JSON.stringify({ nome: editNome.trim(), icona: editIcona }),
        });
      }
      if (!res.ok) throw new Error();
      toast.success("Categoria aggiornata");
      setEditId(null);
      onRefresh();
    } catch { toast.error("Errore nell'aggiornamento"); }
    finally { setSaving(false); }
  };

  // ── Elimina ──
  const handleDelete = async (cat) => {
    const count = prodottiPerCategoria(cat.id);
    if (count > 0) {
      toast.error(`Impossibile eliminare: ${count} prodotti assegnati`);
      return;
    }
    if (!window.confirm(`Eliminare la categoria "${cat.nome}"?`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/${cat.id}`, { method: "DELETE", headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Errore");
      }
      toast.success("Categoria eliminata");
      onRefresh();
    } catch (e) { toast.error(e.message || "Errore nell'eliminazione"); }
    finally { setSaving(false); }
  };

  const handleClose = () => {
    setEditId(null);
    setAddingNew(false);
    setNuovoNome("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isFood ? "🍕 Categorie Food & Beverage" : "📦 Categorie Attrezzature & Packaging"}
          </DialogTitle>
        </DialogHeader>

        {/* Lista categorie esistenti */}
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {categorie.length === 0 && (
            <p className="text-muted-foreground text-center text-sm py-4">Nessuna categoria ancora</p>
          )}
          {categorie.map(cat => {
            const count = prodottiPerCategoria(cat.id);
            const isEditing = editId === cat.id;
            return (
              <div key={cat.id} className="flex items-center gap-2 p-3 rounded-xl bg-muted/30 border border-border/40">
                {isEditing ? (
                  <>
                    {!isFood && (
                      <Input
                        value={editIcona}
                        onChange={e => setEditIcona(e.target.value)}
                        className="w-14 text-center text-lg rounded-lg"
                        maxLength={2}
                      />
                    )}
                    <Input
                      value={editNome}
                      onChange={e => setEditNome(e.target.value)}
                      className="flex-1 rounded-lg"
                      onKeyDown={e => e.key === "Enter" && handleSaveEdit(cat)}
                      autoFocus
                    />
                    <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(cat)} disabled={saving} className="text-emerald-600 hover:text-emerald-700">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="text-muted-foreground">
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="text-lg w-7 text-center">{cat.icona || (isFood ? "🥗" : "📦")}</span>
                    <span className="flex-1 font-medium text-sm">{cat.nome}</span>
                    {count > 0 && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{count}</span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => startEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(cat)}
                      disabled={count > 0}
                      title={count > 0 ? `${count} prodotti assegnati` : "Elimina"}
                      className={count > 0 ? "opacity-30 cursor-not-allowed" : "text-red-500 hover:text-red-600"}
                      data-testid={`delete-cat-${cat.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Aggiungi nuova categoria */}
        {addingNew ? (
          <div className="flex items-center gap-2 mt-3 p-3 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5">
            {!isFood && (
              <Input
                value={nuovaIcona}
                onChange={e => setNuovaIcona(e.target.value)}
                className="w-14 text-center text-lg rounded-lg"
                maxLength={2}
                placeholder="📦"
              />
            )}
            <Input
              value={nuovoNome}
              onChange={e => setNuovoNome(e.target.value)}
              placeholder="Nome categoria"
              className="flex-1 rounded-lg"
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              autoFocus
              data-testid="nuova-categoria-nome"
            />
            <Button size="sm" onClick={handleAdd} disabled={saving} className="rounded-lg" data-testid="btn-conferma-categoria">
              {saving ? "..." : <Check className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAddingNew(false)} className="rounded-lg">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-full mt-3 rounded-xl border-dashed"
            onClick={() => setAddingNew(true)}
            data-testid="btn-aggiungi-categoria"
          >
            <Plus className="h-4 w-4 mr-2" />Aggiungi categoria
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GestioneCategorie;
